import React, { useState, useCallback } from "react";
import { toast } from "react-toastify";

const API_BASE = import.meta.env.VITE_BACKEND_URL || "";

const RAW_EXT_RE = /\.(dng|arw|cr2|cr3|nef|raf|orf|rw2)$/i;
const isVideoType = (t) => t === "video/mp4" || t === "video/webm" || t === "video/quicktime";
const isImageType = (t) => t?.startsWith("image/");
const isRawFile = (file) => RAW_EXT_RE.test(file.name || "");

export default function PhotoUploader({ albumId, onDone }) {
  const [busy, setBusy] = useState(false);
  const [hover, setHover] = useState(false);

  const handleFiles = useCallback(async (files) => {
    if (!albumId) { toast.warning("Abra um álbum válido para enviar."); return; }
    if (!files?.length) return;

    setBusy(true);
    const token = localStorage.getItem("token");

    try {
      for (const file of files) {
        const name = file.name || "arquivo";
        const raw = isRawFile(file);
        const img = isImageType(file.type);
        const vid = isVideoType(file.type);

        if (!(raw || img || vid)) {
          toast.error(`Formato não suportado: ${name} (${file.type || "desconhecido"})`);
          continue;
        }

        const contentType = raw ? "application/octet-stream" : (file.type || (vid ? "video/mp4" : "application/octet-stream"));

        const pres = await fetch(`${API_BASE}/api/photos/presign-put`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ albumId, filename: name, contentType })
        }).then((r) => r.json());
        if (!pres?.url || !pres?.key || !pres?.photoId) throw new Error("Resposta inválida do presign.");

        const putRes = await fetch(pres.url, { method: "PUT", body: file, headers: { "Content-Type": contentType } });
        if (!putRes.ok) throw new Error(`PUT falhou (${putRes.status})`);

        const ing = await fetch(`${API_BASE}/api/photos/ingest`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ albumId, photoId: pres.photoId, key: pres.key }),
        });
        if (!ing.ok) throw new Error(`Ingest falhou (${ ing.status })`);
      }

      toast.success("Upload concluído!");
    } catch (e) {
      console.error(e);
      toast.error("Falha no upload. Tente novamente.");
    } finally {
      setBusy(false);
      onDone && onDone();
    }
  }, [albumId, onDone]);

  const onInputChange = (e) => handleFiles([...e.target.files]);
  const onDrop = (e) => { e.preventDefault(); e.stopPropagation(); setHover(false); handleFiles([...(e.dataTransfer?.files || [])]); };
  const onDrag = (e) => { e.preventDefault(); e.stopPropagation(); setHover(true); };
  const onDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setHover(false); };

  return (
    <div onDrop={onDrop} onDragEnter={onDrag} onDragOver={onDrag} onDragLeave={onDragLeave}
         style={{ border: "1px dashed #aaa", padding: 16, borderRadius: 8, marginBottom: 16, background: hover ? "#f7f4fb" : "#fff", transition: "background .15s" }}>
      <input
        type="file"
        multiple
        accept={[
          "image/*",
          "video/mp4","video/webm","video/quicktime",
          ".dng",".arw",".cr2",".cr3",".nef",".raf",".orf",".rw2",
          "image/x-adobe-dng","image/dng","image/x-sony-arw","image/x-canon-cr2","image/x-canon-cr3",
          "image/x-nikon-nef","image/x-fuji-raf","image/x-olympus-orf","image/x-panasonic-rw2"
        ].join(",")}
        onChange={onInputChange}
        disabled={busy}
        style={{ display: "block", marginBottom: 8 }}
      />
      <small style={{opacity:.75}}>
        Aceita: imagens, vídeos (MP4/WebM/MOV) e RAWs (DNG/ARW/CR2/CR3/NEF/RAF/ORF/RW2). Arraste e solte aqui.
      </small>
      {busy && <p>Enviando...</p>}
    </div>
  );
}
